import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ServermonitorEditor.html?raw';
import NumberInput from '../../moreCore/UI/NumberInput/NumberInput';
import { ExpandableSection, ExpandableSectionMarkings } from "../../moreCore/UI/Container/ExpandableSectionTypes";
import { DiskMonitor, ServerMonitor } from "./ServermonitorTypes";
import StaticTable from '../../moreCore/UI/StaticTable/StaticTable';
import EntityStore from '../../moreCore/Tools/EntityStore';
import lang from 'dojo/_base/lang';
import _StatefulSection from '../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../moreCore/UI/Container/uiFrame';
import HarddriveMonitorEditor from './HarddriveMonitorEditor';
import Edit from '../../moreCore/UI/Edit/Edit';
import on from 'dojo/on';
import Tooltip from '../../moreCore/UI/Floating/Tooltip';

class ServermonitorEditor extends compose(_StatefulSection, _uiFrameMixin) {
    templateString: any = template;

    private inputMemoryUsage: MdsLax<InstanceType<typeof NumberInput>>;
    private inputMemoryAlertValue: MdsLax<InstanceType<typeof NumberInput>>;
    private inputUptimeMinutes: MdsLax<InstanceType<typeof NumberInput>>;
    private inputUptimeAlertValue: MdsLax<InstanceType<typeof NumberInput>>;
    private inputPowerState: MdsLax<InstanceType<typeof Edit>>;

    private btnPowerOnVM: HTMLButtonElement;
    private sectionSecureButtons: HTMLElement;
    private btnResetGuestOSVM: HTMLButtonElement;
    private btnHardResetVM: HTMLButtonElement;
    private btnShutDownGuestOSVM: HTMLButtonElement;
    private btnPowerOffVM: HTMLButtonElement;
    private btnRefreshPowerState: HTMLButtonElement;

    private section2FA: HTMLDivElement;
    private input2FACode: MdsLax<InstanceType<typeof Edit>>;

    private harddriveTableContainer: MdsLax<InstanceType<typeof ExpandableSection>>;

    private activeSecureButton: HTMLButtonElement;
    private activeSecureButtonMode: string;

    private harddriveTable: MdsLax<InstanceType<typeof StaticTable>>;
    private harddriveStore: MdsLax<InstanceType<typeof EntityStore<DiskMonitor>>>;

    private harddriveTableRowConfig: StaticTable.RowConfig = {
        attributes: [
            "is_active",
            "driveletter",
            "tmp_freediskspace",
            "free_space_alert_value",
            "note"
        ],
        colNamesInternal: [
            "is_active",
            "driveletter",
            "tmp_freediskspace",
            "free_space_alert_value",
            "note"
        ],
        headings: [
            (this as any).translate("General_Label_IsActive"),
            (this as any).translate("General_Label_DriveLetter"),
            (this as any).translate("General_Label_FreeDiskSpace"),
            (this as any).translate("General_Label_FreeSpaceAlertValue"),
            (this as any).translate("General_Label_Note"),
        ],
        
    }

    private harddriveTableLayout: StaticTable.Layout = {
        label: "harddriveTable",
        name: "harddriveTable",
        rows: [(this as any).harddriveTableRowConfig]
    } 

    private _tableFormatter(cell: StaticTable.StaticTableCell<DiskMonitor>): boolean {
        let name = cell.name;
        let node = cell.contentnode;
        let value = cell.value;

        switch (name) {
            case "tmp_freediskspace":
            case "free_space_alert_value":
                node.innerHTML = value + " MB";
                return true;
                break;
        }


        return false;
    } 

    private _onRowClick(row: StaticTable.StaticTableRow<DiskMonitor>) {
        let entity = row.rowGroup.entity;
        this._openHarddriveEditor(entity);
    } 

    private _onEditButtonClick(rowGroup: StaticTable.StaticTableRowGroup<DiskMonitor>) {
        let entity = rowGroup.entity;
        this._openHarddriveEditor(entity);
    }

    private _openHarddriveEditor(entity: DiskMonitor) {
        let editor = new HarddriveMonitorEditor();
        let clonedEntity = lang.clone(entity);
        editor.setup(clonedEntity);
        editor.on("confirmed", lang.hitch(this, this._onEditorConfirmed));
    }

    private _onEditorConfirmed(entity: DiskMonitor) {
        this.harddriveStore.changeEntry(entity, true);
        //this.harddriveTable.setupFromStore(this.harddriveStore);
    }

    setup(entity: ServerMonitor, tabContainerName: string) {
        this.internalName = "Servermonitor_" + entity.ID;
        this.showInSidebar = true;
        this.iconclass = "fas fa-server";
        this.show("tab", entity.$Displayname, tabContainerName);

        this.setStartupState("loading");
        this._loadEntityData(entity.ID);
    }  

    private _loadEntityData(entityID: string) {
        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API__ServermonitorEditor_LoadData", { entityID: entityID }, lang.hitch(this, this._onLoadEntityDataResponse), lang.hitch(this, this._onLoadEntityDataError));
    }

    private _onLoadEntityDataResponse(response: DiskMonitor) {
        this.StandbyStop();
        this.setData(response);
    }

    private _onLoadEntityDataError(error) {
        this.StandbyStop();
        console.error(error);
    }

    private _setupHarddriveTable(): void {
        this.harddriveTable = new StaticTable();
        this.harddriveTable.setTableLayout(this.harddriveTableLayout);
        this.harddriveTable.setFormatter(this._tableFormatter);
        this.harddriveTable.setRowClick(lang.hitch(this, this._onRowClick));
        this.harddriveTable.addDefaultRowActionButton("edit", lang.hitch(this, this._onEditButtonClick));
        this.harddriveTable.setupFromStore(this.harddriveStore);
        this.harddriveTable.setCyAttribute("ServermonitorEditorHarddriveTable");
        this.harddriveTable.placeAt(this.harddriveTableContainer);
    }

    private _setHarddriveExpanderMarking() {
        let completeList = this.harddriveStore.getCurrentCompleteList();
        this.harddriveTableContainer.setMarking(completeList.length > 0 ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.empty);
    }

    private _onClickSave() {
        this.StandbyStart();
        let entityID = this.getAttributeValue("ID");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction("Webtool_API__ServermonitorEditor_SaveData", { entityID: entityID, changedData: changedData }, lang.hitch(this, this._onSaveResponse), lang.hitch(this, this._onSaveError))
    }

    private _onSaveResponse(response) {
        this.StandbyStop();
        this._loadEntityData(response.entityID);
    }

    private _onSaveError(error) {
        this.StandbyStop();
        console.error(error);
    }

    private _handleDataButtonState(state: boolean) {
        this.enableButton("rollback", state);
        this.enableButton("save", state);
    }

    private _onClickReloadHarddriveList() {
        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API__ServermonitorEditor_LoadHarddriveListData", { entityID: this.getAttributeValue("ID") }, lang.hitch(this, this._onReloadHarddriveListResponse), lang.hitch(this, this._onReloadHarddriveListError));
    }

    private _onReloadHarddriveListResponse(response: Array<DiskMonitor>) {
        this.StandbyStop();
        this.setAttributeValue("harddriveDiffData", null);
        this.harddriveStore.setup(response);
        this._setHarddriveExpanderMarking();
    }

    private _onReloadHarddriveListError(error) {
        this.StandbyStop();
        console.error(error);
    }

    private _onHarddriveStoreChange() {
        let diffData = lang.clone(this.harddriveStore.getDiffData(true));
        this.setAttributeValue("harddriveDiffData", diffData);
    }

    private _resetSecureButton() {
        if (this.activeSecureButton) this.UiModder.domClass.remove(this.activeSecureButton, "active-button");
        this.activeSecureButton = null;
        this.activeSecureButtonMode = null;
        this.hideNode(this.section2FA, true);
        this.input2FACode.setValue("");
    }

    private _onClickSecureButton(mode: string, evt: MouseEvent) {
        if (this.activeSecureButton) this.UiModder.domClass.remove(this.activeSecureButton, "active-button");
        let targetButton = evt.target;
        if (targetButton !== this.activeSecureButton) {
            this.activeSecureButton = targetButton as HTMLButtonElement;
            if (this.activeSecureButton) this.UiModder.domClass.add(this.activeSecureButton, "active-button");
            this.activeSecureButtonMode = mode;

            this.hideNode(this.section2FA, false);
        } else {
            this._resetSecureButton();
        }
    }

    private _onClickSendButton(evt: MouseEvent) {
        let code = this.input2FACode.getValue();

        this.RequestManager.callfunction(
            "Webtool_API__ServermonitorEditor_Check2FA",
            { code: code, mode: this.activeSecureButtonMode, vmId: this.getAttributeValue("vcenter_vm_id"), entityId: this.getAttributeValue("ID") },
            lang.hitch(this, this._onValidateResponse),
            lang.hitch(this, this._onValidateError)
        )
    }

    private _onValidateResponse(response) {
        let error = response.error;
        let success = response.value;

        console.log("success");
        this.input2FACode.setValue("");
        this._resetSecureButton();
    }

    private _onValidateError(error) {
        this.showToast(error.Message, "fas fa-exclamation-triangle");
        console.error(error);
    }

    private _onClickRefreshPowerState() {
        this.inputPowerState.StandbyStart();
        this.RequestManager.callfunction(
            "Webtool_API__ServermonitorEditor_GetPowerState",
            { vmId: this.getAttributeValue("vcenter_vm_id") },
            lang.hitch(this, this._onRefreshPowerStateResponse),
            lang.hitch(this, this._onRefreshPowerStateError)
        )
    }

    private _onRefreshPowerStateResponse(response) {
        this.inputPowerState.StandbyStop();
        this.inputPowerState.setValue(response.value);
    }

    private _onRefreshPowerStateError(error) {
        this.inputPowerState.StandbyStop();
        this.showToast(error.Message, "fas fa-exclamation-triangle");
        console.error(error);
    }

    private _onClickStartVM() {
        this.inputPowerState.StandbyStart()
        this.RequestManager.callfunction(
            "Webtool_API__ServerMonitorEditor_StartVM",
            { vmId: this.getAttributeValue("vcenter_vm_id") },
            lang.hitch(this, this._onStartVMResponse),
            lang.hitch(this, this._onStartVMError)
        )
    }

    private _onStartVMResponse(response) {
        this.inputPowerState.StandbyStop();
        console.log(response);
        this._onClickRefreshPowerState();
    }

    private _onStartVMError(error) {
        console.error(error);
        this.inputPowerState.StandbyStop();
        this.showToast(error.Message, "fas fa-exclamation-triangle");
       
    }



    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {

        }
    }

    protected onDirtyChange(newState: boolean) {
        this._handleDataButtonState(newState);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);



        this.setAttributeValue("harddriveDiffData", null);

        let harddriveData = this.getAttributeValue("EL_remote_disk_monitoring_remote_machine");
        this.harddriveStore.setup(harddriveData);
        this._setHarddriveExpanderMarking();

        let id = this.getAttributeValue("ID");
        this.createDefaultButton("reload", lang.hitch(this, this._loadEntityData, id));
        this.createDefaultButton("rollback", lang.hitch(this, this.rollback), false);
        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), false);

        let powerState = this.getAttributeValue("power_state_vm");
        this.inputPowerState.setValue(powerState);
    }

    private _onChangePowerState() {
        this._colorPowerStateInput();
    }

    private _colorPowerStateInput() {
        let powerState = this.inputPowerState.getValue();
        let colorClass = powerState === "POWERED_ON" ? "w3-green" : "w3-red";
        let oldColorClass = powerState !== "POWERED_ON" ? "w3-green" : "w3-red";
        this.UiModder.domClass.remove(this.inputPowerState.inputNode, oldColorClass);
        this.UiModder.domClass.add(this.inputPowerState.inputNode, colorClass);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.inputMemoryUsage.setPlaces(2);
        this.inputMemoryUsage.setStep("0.01");
        this.inputMemoryUsage.setUnit("%");

        this.inputMemoryAlertValue.setPlaces(2);
        this.inputMemoryAlertValue.setStep("0.01");
        this.inputMemoryAlertValue.setUnit("%");

        this.inputUptimeMinutes.setUnit("min");
        this.inputUptimeAlertValue.setUnit("min");

        on(this.btnResetGuestOSVM, "click", lang.hitch(this, this._onClickSecureButton, "resetGuestOSVM"));
        let tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_ResetGuestOSVM"), targetnode: this.btnResetGuestOSVM, enableClick: true });

        on(this.btnHardResetVM, "click", lang.hitch(this, this._onClickSecureButton, "hardResetVM"));
        tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_HardResetVM"), targetnode: this.btnHardResetVM, enableClick: true });

        on(this.btnShutDownGuestOSVM, "click", lang.hitch(this, this._onClickSecureButton, "shutdownGuestOSVM"));
        tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_ShutDownGuestOSVM"), targetnode: this.btnShutDownGuestOSVM, enableClick: true });

        on(this.btnPowerOffVM, "click", lang.hitch(this, this._onClickSecureButton, "powerOffVM"));
        tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_PowerOffVM"), targetnode: this.btnPowerOffVM, enableClick: true });

        tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_PowerOnVM"), targetnode: this.btnPowerOnVM, enableClick: true });

        tooltip = new Tooltip();
        tooltip.setup({ content: this.translate("Servermonitor_Tooltip_RefreshPowerState"), targetnode: this.btnRefreshPowerState, enableClick: true });

        this.harddriveStore = new EntityStore();
        this.harddriveStore.on("change", lang.hitch(this, this._onHarddriveStoreChange));
        this._setupHarddriveTable();

        if (!this.hasUserRole("Servermonitor_SecureButton")) {
            this.UiModder.domConstruct.destroy(this.sectionSecureButtons);
            this.UiModder.domConstruct.destroy(this.btnResetGuestOSVM);
        }
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(ServermonitorEditor);